import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort } from "@/lib/bookingDates";
import { useCurrentUser } from "@/lib/auth";
import { formatApiError } from "@/lib/formatApiError";
import { canWriteStammdaten } from "@/lib/permissions";
import {
  cancelTouristImportJob,
  getTouristImportJob,
  getTouristImportJobMappings,
  reconcileTouristArticleMappings,
  saveTouristUploadMappings,
  getTouristUpload,
  getTouristUploadSession,
  getTouristImportErrorReport,
  getTouristImportStats,
  listTouristImportErrors,
  normalizePlaceId,
  normalizeTouristImportPreview,
  parseJobErrorSummary,
  listTouristUploads,
  pollTouristImportJob,
  startTouristImport,
  startTouristPreview,
  startTouristSqlUploadSession,
  uploadTouristSqlFile,
  type ArrivalTypeMappingPayload,
  type ArticleMappingPayload,
  type CategoryMappingPayload,
  type PlaceMappingPayload,
  type TouristCode,
  type TouristImportError,
  type TouristImportErrorReport,
  type TouristImportErrorSummary,
  type TouristImportJob,
  type TouristImportMappingsPayload,
  type TouristImportStats,
  type TouristImportPreview,
  type TouristImportProgress,
  type TouristImportRunResult,
  type TouristImportReconcileArticlesResult,
  type TouristImportUploadSummary,
  type TouristMappingSuggestion,
  type TouristPlace,
} from "@/lib/touristImportApi";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { TouristImportSoldOutPanel } from "@/pages/stammdaten/tourist-import/TouristImportSoldOutPanel";
import { TouristPanelPagesPanel } from "@/pages/stammdaten/tourist-import/TouristPanelPagesPanel";
import { TouristPanelWorkstationsPanel } from "@/pages/stammdaten/tourist-import/TouristPanelWorkstationsPanel";
import { classNames } from "@/lib/format";
import { sortPitchesByTouristPlanr } from "@/lib/touristImportMatching";
import type { ArrivalTypeRecord, Article, Pitch, PitchCategory } from "@/types/camping";

const TOURIST_TABLE_LABELS: Record<string, string> = {
  adr: "Gäste",
  rec: "Buchungen / Rechnungen",
  adp: "Platzbelegungen",
  pla: "Tourist-Plätze",
  zei: "Rechnungspositionen",
  art: "Tourist-Artikel",
  kfz: "Kennzeichen",
};

const PHASE_LABELS: Record<string, string> = {
  upload: "Hochladen",
  analyze: "Analyse",
  import: "Import",
  error: "Fehler",
};

type TouristImportTab = "import" | "sold-out" | "pages" | "devices";

const TOURIST_IMPORT_TABS: { id: TouristImportTab; label: string }[] = [
  { id: "import", label: "Datenimport" },
  { id: "sold-out", label: "Ausgebuchte Nächte" },
  { id: "pages", label: "Seiten" },
  { id: "devices", label: "Geräte" },
];

function parseTouristImportTab(raw: string | null): TouristImportTab {
  if (raw === "sold-out" || raw === "pages" || raw === "devices") return raw;
  return "import";
}

type PlaceMappingRow = PlaceMappingPayload & { label: string };

type ArticleMappingRow = ArticleMappingPayload & { label: string };

/** Kürzel-Zuordnung (Platzkategorie adr14 / Anreiseart adr22) → Ziel-ID. */
type CodeMappingRow = {
  tourist_code: string;
  guest_count: number;
  target_id: string;
};

type SavedMappings = {
  place_mappings?: PlaceMappingPayload[];
  article_mappings?: ArticleMappingPayload[];
  category_mappings?: CategoryMappingPayload[];
  arrival_type_mappings?: ArrivalTypeMappingPayload[];
  place_mapping_issues?: string[];
};

function buildCodeMappings(
  codes: TouristCode[],
  suggestions: TouristMappingSuggestion[],
  saved: { tourist_code: string; target_id: string }[] = [],
): CodeMappingRow[] {
  const savedByCode = new Map(saved.map((m) => [m.tourist_code.toUpperCase(), m.target_id]));
  const suggestedByCode = new Map(
    suggestions.map((s) => [s.tourist_key.toUpperCase(), s.target_id]),
  );
  const rows = codes.map((code) => ({
    tourist_code: code.tourist_code,
    guest_count: code.guest_count,
    target_id: normalizePlaceId(
      savedByCode.get(code.tourist_code.toUpperCase()) ??
        suggestedByCode.get(code.tourist_code.toUpperCase()) ??
        "",
    ),
  }));
  const known = new Set(rows.map((r) => r.tourist_code.toUpperCase()));
  for (const m of saved) {
    if (!known.has(m.tourist_code.toUpperCase())) {
      rows.push({
        tourist_code: m.tourist_code,
        guest_count: 0,
        target_id: normalizePlaceId(m.target_id),
      });
    }
  }
  return rows;
}

function suggestionsByPlaid(
  suggestions: TouristMappingSuggestion[],
): Map<number, string> {
  const map = new Map<number, string>();
  for (const s of suggestions) {
    const plaid = Number.parseInt(s.tourist_key, 10);
    if (!Number.isNaN(plaid) && !map.has(plaid)) {
      map.set(plaid, s.target_id);
    }
  }
  return map;
}

function buildPlaceMappings(
  preview: TouristImportPreview,
  savedMappings: PlaceMappingPayload[] = [],
): PlaceMappingRow[] {
  const savedByPlaid = new Map(savedMappings.map((m) => [m.tourist_plaid, m]));
  const suggestedTargets = suggestionsByPlaid(preview.place_mapping_suggestions ?? []);

  const touristPlaces: TouristPlace[] =
    (preview.tourist_places?.length ?? 0) > 0
      ? preview.tourist_places!
      : (preview.place_mapping_suggestions ?? [])
          .map((s) => {
            const plaid = Number.parseInt(s.tourist_key, 10);
            if (Number.isNaN(plaid)) return null;
            return { tourist_plaid: plaid, tourist_planr: s.tourist_label };
          })
          .filter((p): p is TouristPlace => p !== null);

  const byPlaid = new Map<number, PlaceMappingRow>();
  for (const place of touristPlaces) {
    const saved = savedByPlaid.get(place.tourist_plaid);
    const target = normalizePlaceId(
      saved?.target_place_id ?? suggestedTargets.get(place.tourist_plaid) ?? "",
    );
    byPlaid.set(place.tourist_plaid, {
      tourist_plaid: place.tourist_plaid,
      tourist_planr: saved?.tourist_planr ?? place.tourist_planr,
      target_place_id: target,
      label: place.tourist_planr,
    });
  }

  for (const saved of savedMappings) {
    if (!byPlaid.has(saved.tourist_plaid)) {
      byPlaid.set(saved.tourist_plaid, {
        ...saved,
        label: saved.tourist_planr,
      });
    }
  }

  return [...byPlaid.values()].sort((a, b) =>
    a.tourist_planr.localeCompare(b.tourist_planr, "de"),
  );
}

function suggestionsByArtid(
  suggestions: TouristMappingSuggestion[],
): Map<number, string> {
  const map = new Map<number, string>();
  for (const s of suggestions) {
    const artid = Number.parseInt(s.tourist_key, 10);
    if (!Number.isNaN(artid) && !map.has(artid)) {
      map.set(artid, s.target_id);
    }
  }
  return map;
}

function articleLabel(
  artnr: string | null | undefined,
  bezeichnung: string | null | undefined,
  artid: number,
): string {
  if (bezeichnung) return bezeichnung;
  if (artnr) return artnr;
  return String(artid);
}

function touristArticlesFromPreview(
  preview: TouristImportPreview,
): Array<{
  tourist_artid: number;
  tourist_artnr: string | null;
  tourist_bezeichnung: string | null;
}> {
  const byArtid = new Map<
    number,
    { tourist_artid: number; tourist_artnr: string | null; tourist_bezeichnung: string | null }
  >();

  for (const article of preview.tourist_articles ?? []) {
    byArtid.set(article.tourist_artid, {
      tourist_artid: article.tourist_artid,
      tourist_artnr: article.tourist_artnr ?? null,
      tourist_bezeichnung: article.tourist_bezeichnung ?? null,
    });
  }

  for (const suggestion of preview.article_mapping_suggestions ?? []) {
    const artid = Number.parseInt(suggestion.tourist_key, 10);
    if (Number.isNaN(artid) || byArtid.has(artid)) continue;
    byArtid.set(artid, {
      tourist_artid: artid,
      tourist_artnr: null,
      tourist_bezeichnung: suggestion.tourist_label ?? null,
    });
  }

  return [...byArtid.values()];
}

function buildArticleMappings(
  preview: TouristImportPreview,
  savedMappings: ArticleMappingPayload[] = [],
): ArticleMappingRow[] {
  const savedByArtid = new Map(savedMappings.map((m) => [m.tourist_artid, m]));
  const suggestedTargets = suggestionsByArtid(preview.article_mapping_suggestions ?? []);

  const touristArticles = touristArticlesFromPreview(preview);

  const byArtid = new Map<number, ArticleMappingRow>();
  for (const article of touristArticles) {
    const saved = savedByArtid.get(article.tourist_artid);
    const target = normalizePlaceId(
      saved?.target_article_id ?? suggestedTargets.get(article.tourist_artid) ?? "",
    );
    const label = articleLabel(
      saved?.tourist_artnr ?? article.tourist_artnr,
      saved?.tourist_bezeichnung ?? article.tourist_bezeichnung,
      article.tourist_artid,
    );
    byArtid.set(article.tourist_artid, {
      tourist_artid: article.tourist_artid,
      tourist_artnr: saved?.tourist_artnr ?? article.tourist_artnr ?? null,
      tourist_bezeichnung: saved?.tourist_bezeichnung ?? article.tourist_bezeichnung ?? null,
      target_article_id: target,
      label,
    });
  }

  for (const saved of savedMappings) {
    if (!byArtid.has(saved.tourist_artid)) {
      byArtid.set(saved.tourist_artid, {
        ...saved,
        label: articleLabel(saved.tourist_artnr, saved.tourist_bezeichnung, saved.tourist_artid),
      });
    }
  }

  return [...byArtid.values()].sort((a, b) => a.label.localeCompare(b.label, "de"));
}

function progressLabel(progress: TouristImportProgress | null): string | null {
  if (!progress?.message) return null;
  const phase = progress.phase ? PHASE_LABELS[progress.phase] ?? progress.phase : null;
  return phase ? `${phase}: ${progress.message}` : progress.message;
}


const SOURCE_TABLE_LABELS: Record<string, string> = {
  adr: "Gäste (adr)",
  rec: "Buchungen/Rechnungen (rec)",
  zei: "Rechnungspositionen (zei)",
  adp: "Platzbelegungen (adp)",
  kfz: "Kennzeichen (kfz)",
};

const STAT_LABELS: Record<string, string> = {
  guests: "Gäste importiert",
  reservations: "Reservierungen",
  stays: "Aufenthalte",
  invoices: "Rechnungen",
  occupancies: "Platzbelegungen",
  check_ins: "Aktuelle Belegungen eingecheckt",
  invoice_lines: "Rechnungspositionen",
  payments: "Zahlungen importiert",
  payments_storno_skipped: "Storno-Zahlungen übersprungen",
  payments_storno_imported: "Storno-Zahlungen importiert",
  cash_withdrawals: "Kassenentnahmen",
  license_plates: "Kennzeichen",
  reconcile_invoices: "Rechnungen abgeglichen",
  skipped: "Zeilen übersprungen",
  skipped_existing: "Bereits vorhanden (übersprungen)",
  bookings_inferred_stay: "Aufenthalte ohne T-/R-Präfix (referenziert)",
  errors: "Fehler gesamt",
  errors_suppressed: "Fehler (nicht einzeln geloggt)",
  place_mappings_used: "Platz-Zuordnungen aktiv",
};

function formatImportStatKey(key: string): string {
  return STAT_LABELS[key] ?? key;
}

function formatSourceTable(table: string): string {
  return SOURCE_TABLE_LABELS[table] ?? table;
}

function errorHint(report: TouristImportErrorReport | null): string | null {
  if (!report || report.summary.length === 0) return null;
  const top = report.summary[0];
  if (!top) return null;
  if (top.field_name === "recadr" || top.message.includes("Gast nicht gefunden")) {
    return "Viele Buchungen verweisen auf Gäste (adrid), die nicht importiert wurden. Prüfen Sie die adr-Tabelle im Dump und ob der Gäste-Import vollständig war.";
  }
  if (top.field_name === "rec03" || top.field_name === "adppla" || top.message.includes("Platz-Mapping")) {
    return "Platz-Zuordnungen gelten pro Upload-Sitzung. Speichern Sie die Zuordnung vor dem Import und prüfen Sie auch rec03-Platz-IDs, die nur in Buchungen vorkommen.";
  }
  if (top.field_name === "adprec" || top.message.includes("Buchung nicht gefunden")) {
    return "Platzbelegungen (adp) verweisen auf Buchungen (rec), die noch fehlen. Prüfen Sie, ob der rec-Import Aufenthalte angelegt hat (recnr mit T- oder referenzierte recid in adp/zei/kfz). Nach einem Fix Import erneut starten – bereits importierte Gäste werden übersprungen.";
  }
  if (top.field_name === "zeirec" || top.message.includes("Rechnung nicht gefunden")) {
    return "Rechnungspositionen (zei) hängen von importierten Rechnungen/Buchungen (rec) ab. Beheben Sie zuerst Fehler in rec, dann Import erneut starten.";
  }
  if (top.field_name === "rec02/rec03" || top.field_name === "rec01/rec02" || top.message.includes("Datum")) {
    return "Viele Datensätze haben ungültige oder leere An-/Abreisedaten. Prüfen Sie rec02/rec03/rec04 (oder rec01/rec02) bzw. adp01/adp02 im Dump.";
  }
  return null;
}

export function TouristImportPage({
  inIntegration = false,
  canWrite: canWriteProp,
}: {
  inIntegration?: boolean;
  canWrite?: boolean;
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTouristImportTab(searchParams.get("tab"));
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteProp ?? canWriteStammdaten(groups);

  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [recentUploads, setRecentUploads] = useState<TouristImportUploadSummary[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [pitchCategories, setPitchCategories] = useState<PitchCategory[]>([]);
  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const [preview, setPreview] = useState<TouristImportPreview | null>(null);
  const [placeMappings, setPlaceMappings] = useState<PlaceMappingRow[]>([]);
  const [articleMappings, setArticleMappings] = useState<ArticleMappingRow[]>([]);
  const [categoryMappings, setCategoryMappings] = useState<CodeMappingRow[]>([]);
  const [arrivalTypeMappings, setArrivalTypeMappings] = useState<CodeMappingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [progress, setProgress] = useState<TouristImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<TouristImportRunResult | null>(null);
  const [importErrors, setImportErrors] = useState<TouristImportError[]>([]);
  const [importErrorSummary, setImportErrorSummary] = useState<TouristImportErrorSummary[]>([]);
  const [importErrorReport, setImportErrorReport] = useState<TouristImportErrorReport | null>(null);
  const [importStats, setImportStats] = useState<TouristImportStats | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [reconcileMessage, setReconcileMessage] = useState<string | null>(null);
  const [reconcilingArticles, setReconcilingArticles] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [connectionRetryCount, setConnectionRetryCount] = useState(0);
  const [cancellingImport, setCancellingImport] = useState(false);

  const syncUrl = useCallback(
    (nextUploadId: string | null, nextJobId: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (nextUploadId) params.set("upload", nextUploadId);
      else params.delete("upload");
      if (nextJobId) params.set("job", nextJobId);
      else params.delete("job");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (inIntegration) return;
    const subtitles: Record<TouristImportTab, string> = {
      import: "Tourist-Import",
      "sold-out": "Tourist-Anbindung · Ausgebuchte Nächte",
      pages: "Tourist-Panel · Seiten",
      devices: "Tourist-Panel · Geräte",
    };
    setPageMeta({
      title: "Stammdaten",
      subtitle: subtitles[tab],
    });
    return () => setPageMeta(null);
  }, [inIntegration, setPageMeta, tab]);

  function setTab(next: TouristImportTab) {
    const params = new URLSearchParams(searchParams);
    if (next === "import") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params);
  }

  useEffect(() => {
    api
      .get<Pitch[]>("/api/v1/pitches")
      .then(setPitches)
      .catch(() => setPitches([]));
    api
      .get<Article[]>("/api/v1/articles?include_inactive=true")
      .then(setArticles)
      .catch(() => setArticles([]));
    api
      .get<PitchCategory[]>("/api/v1/pitch-categories")
      .then(setPitchCategories)
      .catch(() => setPitchCategories([]));
    api
      .get<ArrivalTypeRecord[]>("/api/v1/arrival-types")
      .then(setArrivalTypes)
      .catch(() => setArrivalTypes([]));
  }, []);

  const refreshRecentUploads = useCallback(() => {
    listTouristUploads()
      .then(setRecentUploads)
      .catch(() => setRecentUploads([]));
  }, []);

  useEffect(() => {
    if (canEdit) refreshRecentUploads();
  }, [canEdit, refreshRecentUploads]);

  const applyPreview = useCallback(
    (result: TouristImportPreview, saved: SavedMappings = {}) => {
      const normalized = normalizeTouristImportPreview(result);
      if (saved.place_mapping_issues !== undefined) {
        normalized.place_mapping_issues = saved.place_mapping_issues;
      }
      setPreview(normalized);
      setPlaceMappings(buildPlaceMappings(normalized, saved.place_mappings ?? []));
      setArticleMappings(buildArticleMappings(normalized, saved.article_mappings ?? []));
      setCategoryMappings(
        buildCodeMappings(
          normalized.tourist_categories,
          normalized.category_mapping_suggestions,
          (saved.category_mappings ?? []).map((m) => ({
            tourist_code: m.tourist_code,
            target_id: m.target_category_id,
          })),
        ),
      );
      setArrivalTypeMappings(
        buildCodeMappings(
          normalized.tourist_arrival_types,
          normalized.arrival_type_mapping_suggestions,
          (saved.arrival_type_mappings ?? []).map((m) => ({
            tourist_code: m.tourist_code,
            target_id: m.target_arrival_type_id,
          })),
        ),
      );
      setPreviewJobId(normalized.job_id);
      setRunResult(null);
      setImportErrors([]);
      setImportErrorSummary([]);
      setImportStats(null);
    },
    [],
  );

  const applyJobProgress = useCallback(
    (job: TouristImportJob) => {
      setConnectionRetryCount(0);
      setProgress(job.progress);
      if (job.status === "preview" && job.preview_summary) {
        void getTouristImportJobMappings(job.id)
          .then((mappings) => applyPreview(job.preview_summary!, mappings))
          .catch(() => applyPreview(job.preview_summary!));
      }
    },
    [applyPreview],
  );

  const pollOpts = useCallback(
    (intervalMs: number) => ({
      onUpdate: applyJobProgress,
      onTransientError: (failures: number) => {
        setConnectionRetryCount(failures);
        setProgress((prev) => ({
          phase: prev?.phase ?? "import",
          percent: prev?.percent ?? 0,
          message: `Verbindung unterbrochen – erneuter Versuch (${failures}) …`,
        }));
      },
      intervalMs,
    }),
    [applyJobProgress],
  );

  const finishImportJob = useCallback(
    async (job: TouristImportJob) => {
      if (job.status === "failed") {
        setProgress(job.progress);
        setError(job.error_message ?? "Import fehlgeschlagen.");
        return;
      }
      const jobStats = job.stats ?? {};
      const summaryFromJob = parseJobErrorSummary(jobStats);
      const totalErrorsFromJob =
        typeof jobStats.errors === "number" ? Number(jobStats.errors) : 0;

      setRunResult({
        job_id: job.id,
        status: job.status,
        stats: jobStats,
        error_count: totalErrorsFromJob,
      });
      if (summaryFromJob.length > 0) setImportErrorSummary(summaryFromJob);
      setProgress(job.progress);

      const [reportResult, errorsResult, statsResult] = await Promise.allSettled([
        getTouristImportErrorReport(job.id),
        listTouristImportErrors(job.id, { limit: 100 }),
        getTouristImportStats(),
      ]);

      let postLoadWarning: string | null = null;

      if (reportResult.status === "fulfilled") {
        const report = reportResult.value;
        const mergedSummary =
          summaryFromJob.length > 0 ? summaryFromJob : report.summary;
        const totalErrors =
          totalErrorsFromJob > 0 ? totalErrorsFromJob : report.total_errors;
        setRunResult({
          job_id: job.id,
          status: job.status,
          stats: jobStats,
          error_count: totalErrors,
        });
        if (mergedSummary.length > 0) setImportErrorSummary(mergedSummary);
        setImportErrorReport({
          ...report,
          total_errors: totalErrors,
          summary: mergedSummary,
        });
      } else {
        postLoadWarning = formatApiError(
          reportResult.reason,
          "Fehlerbericht konnte nicht geladen werden.",
        );
        if (summaryFromJob.length > 0) {
          setImportErrorReport({
            total_errors: totalErrorsFromJob,
            logged_samples: 0,
            suppressed: Number(jobStats.errors_suppressed ?? 0),
            place_mappings_used: Number(jobStats.place_mappings_used ?? 0),
            summary: summaryFromJob,
          });
        }
      }

      if (errorsResult.status === "fulfilled" && errorsResult.value.items.length > 0) {
        setImportErrors(errorsResult.value.items);
      } else if (errorsResult.status === "rejected" && !postLoadWarning) {
        postLoadWarning = formatApiError(
          errorsResult.reason,
          "Fehlerliste konnte nicht geladen werden.",
        );
      }

      if (statsResult.status === "fulfilled") {
        setImportStats(statsResult.value);
      } else if (statsResult.status === "rejected" && !postLoadWarning) {
        postLoadWarning = formatApiError(
          statsResult.reason,
          "Import-Statistik konnte nicht geladen werden.",
        );
      }

      if (postLoadWarning) {
        setError(
          `${postLoadWarning} Der Import auf dem Server ist abgeschlossen – Zusammenfassung unten; bei Bedarf Seite neu laden.`,
        );
      } else {
        setError(null);
      }

      refreshRecentUploads();
    },
    [refreshRecentUploads],
  );

  const resumeUploadSession = useCallback(
    async (id: string) => {
      setResuming(true);
      setError(null);
      setUploadId(id);
      syncUrl(id, null);

      try {
        const session = await getTouristUploadSession(id);
        setProgress(session.upload.progress);

        if (session.upload.status === "uploading") {
          const uploadPoll = await pollUploadUntilReady(id, (p) => setProgress(p));
          setProgress(uploadPoll);
        }

        const activeJob = session.active_job;
        if (activeJob?.status === "running") {
          setPreviewJobId(activeJob.id);
          syncUrl(id, activeJob.id);
          setBusy(true);
          setProgress(activeJob.progress);
          const finished = await pollTouristImportJob(activeJob.id, {
            untilStatuses: ["completed", "failed"],
            ...pollOpts(1000),
          });
          await finishImportJob(finished);
          return;
        }

        if (session.preview_job) {
          const job = session.preview_job;
          setPreviewJobId(job.id);
          syncUrl(id, job.id);

          if (job.status === "analyzing") {
            const finished = await pollTouristImportJob(job.id, {
              untilStatuses: ["preview", "failed"],
              ...pollOpts(800),
            });
            if (finished.status === "failed") {
              setError(finished.error_message ?? "Die Dateiprüfung ist fehlgeschlagen.");
              return;
            }
            if (finished.preview_summary) {
              applyPreview(finished.preview_summary, session.mappings);
            }
          } else if (job.status === "preview" && job.preview_summary) {
            applyPreview(job.preview_summary, session.mappings);
            setProgress(job.progress);
          }
        } else if (activeJob?.status === "analyzing") {
          setPreviewJobId(activeJob.id);
          syncUrl(id, activeJob.id);
          setBusy(true);
          const finished = await pollTouristImportJob(activeJob.id, {
            untilStatuses: ["preview", "failed"],
            ...pollOpts(800),
          });
          if (finished.status === "failed") {
            setError(finished.error_message ?? "Die Dateiprüfung ist fehlgeschlagen.");
            return;
          }
          if (finished.preview_summary) {
            applyPreview(finished.preview_summary, session.mappings);
          }
        }
      } catch (e) {
        setError(formatApiError(e, "Import-Sitzung konnte nicht geladen werden."));
      } finally {
        setBusy(false);
        setResuming(false);
      }
    },
    [applyPreview, finishImportJob, pollOpts, syncUrl],
  );

  const resumeImportJob = useCallback(
    async (jobId: string) => {
      setResuming(true);
      setError(null);
      setConnectionRetryCount(0);
      setPreviewJobId(jobId);
      try {
        const job = await getTouristImportJob(jobId);
        if (job.upload_id) {
          setUploadId(job.upload_id);
          syncUrl(job.upload_id, jobId);
        }
        setProgress(job.progress);
        if (job.status === "completed") {
          await finishImportJob(job);
          return;
        }
        if (job.status === "failed") {
          setError(job.error_message ?? "Import fehlgeschlagen.");
          return;
        }
        if (job.status === "preview" && job.preview_summary) {
          applyPreview(job.preview_summary);
          return;
        }
        if (job.status === "running" || job.status === "analyzing") {
          setBusy(true);
          const untilStatuses =
            job.status === "running" ? ["completed", "failed"] : ["preview", "failed"];
          const finished = await pollTouristImportJob(jobId, {
            untilStatuses,
            ...pollOpts(job.status === "running" ? 1000 : 800),
          });
          if (job.status === "running") {
            await finishImportJob(finished);
          } else if (finished.status === "failed") {
            setError(finished.error_message ?? "Die Dateiprüfung ist fehlgeschlagen.");
          } else if (finished.preview_summary) {
            applyPreview(finished.preview_summary);
          }
        }
      } catch (e) {
        setError(formatApiError(e, "Import-Job konnte nicht geladen werden."));
      } finally {
        setBusy(false);
        setResuming(false);
      }
    },
    [applyPreview, finishImportJob, pollOpts, syncUrl],
  );

  useEffect(() => {
    if (!canEdit) return;
    const urlUpload = searchParams.get("upload");
    if (!urlUpload || urlUpload === uploadId) return;
    void resumeUploadSession(urlUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei URL-Änderung
  }, [canEdit, searchParams.get("upload")]);

  useEffect(() => {
    if (!canEdit) return;
    const urlJob = searchParams.get("job");
    if (!urlJob || urlJob === previewJobId || busy || resuming) return;
    void resumeImportJob(urlJob);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei URL-Änderung
  }, [canEdit, searchParams.get("job")]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = e.target.files?.[0] ?? null;
      setFile(picked);
      setUploadId(null);
      setPreviewJobId(null);
      setPreview(null);
      setPlaceMappings([]);
      setArticleMappings([]);
      setCategoryMappings([]);
      setArrivalTypeMappings([]);
      setRunResult(null);
      setImportErrors([]);
      setImportErrorSummary([]);
      setImportStats(null);
      setProgress(null);
      setError(null);
      syncUrl(null, null);
    },
    [syncUrl],
  );

  function currentMappingsPayload(): TouristImportMappingsPayload {
    return {
      place_mappings: placeMappings.map(({ tourist_plaid, tourist_planr, target_place_id }) => ({
        tourist_plaid,
        tourist_planr,
        target_place_id,
      })),
      article_mappings: articleMappings
        .filter((m) => m.target_article_id)
        .map(({ tourist_artid, tourist_artnr, tourist_bezeichnung, target_article_id }) => ({
          tourist_artid,
          tourist_artnr,
          tourist_bezeichnung,
          target_article_id,
        })),
      category_mappings: categoryMappings
        .filter((m) => m.target_id)
        .map((m) => ({ tourist_code: m.tourist_code, target_category_id: m.target_id })),
      arrival_type_mappings: arrivalTypeMappings
        .filter((m) => m.target_id)
        .map((m) => ({ tourist_code: m.tourist_code, target_arrival_type_id: m.target_id })),
    };
  }

  function currentSavedMappings(): SavedMappings {
    const payload = currentMappingsPayload();
    return {
      place_mappings: payload.place_mappings,
      article_mappings: payload.article_mappings,
      category_mappings: payload.category_mappings,
      arrival_type_mappings: payload.arrival_type_mappings,
    };
  }

  async function pollUploadUntilReady(
    id: string,
    onUpdate: (p: TouristImportProgress) => void,
  ): Promise<TouristImportProgress> {
    for (;;) {
      const upload = await getTouristUpload(id);
      onUpdate(upload.progress);
      if (upload.status === "ready") {
        return { phase: "upload", percent: 100, message: "Upload abgeschlossen." };
      }
      if (upload.status === "failed") {
        throw new Error(upload.error_message ?? "Upload fehlgeschlagen.");
      }
      await new Promise((r) => setTimeout(r, 600));
    }
  }

  async function onUploadAndPreview() {
    if (!file) {
      setError("Bitte zuerst eine .sql-Datei auswählen.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".sql")) {
      setError("Nur SQL-Dump-Dateien (.sql) werden unterstützt.");
      return;
    }

    setBusy(true);
    setError(null);
    setPreview(null);
    setRunResult(null);
    setProgress({ phase: "upload", percent: 0, message: "Upload wird vorbereitet …" });

    try {
      const uploadSession = await startTouristSqlUploadSession(file);
      setUploadId(uploadSession.upload_id);
      syncUrl(uploadSession.upload_id, null);

      await uploadTouristSqlFile(uploadSession.upload_id, file, (pct) => {
        setProgress({
          phase: "upload",
          percent: pct,
          message: `Datei wird hochgeladen (${pct} %) …`,
        });
      });

      const uploadPoll = await pollUploadUntilReady(uploadSession.upload_id, (p) => setProgress(p));
      setProgress(uploadPoll);

      setProgress({ phase: "analyze", percent: 0, message: "Analyse wird gestartet …" });
      const mappingsPayload = placeMappings.length > 0 ? currentMappingsPayload() : undefined;

      const previewStart = await startTouristPreview(uploadSession.upload_id, mappingsPayload);
      setPreviewJobId(previewStart.job_id);
      syncUrl(uploadSession.upload_id, previewStart.job_id);
      setProgress(previewStart.progress);

      const job = await pollTouristImportJob(previewStart.job_id, {
        untilStatuses: ["preview", "failed"],
        ...pollOpts(800),
      });

      if (job.status === "failed") {
        setError(job.error_message ?? "Die Dateiprüfung ist fehlgeschlagen.");
        setProgress(job.progress);
        return;
      }
      if (!job.preview_summary) {
        setError("Analyse abgeschlossen, aber keine Vorschaudaten erhalten.");
        return;
      }
      applyPreview(
        job.preview_summary,
        placeMappings.length > 0 ? currentSavedMappings() : {},
      );
      setProgress(job.progress);
      refreshRecentUploads();
    } catch (e) {
      setError(formatApiError(e, "Upload oder Prüfung fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  async function onStartPreviewOnly() {
    if (!uploadId) return;
    setBusy(true);
    setError(null);
    setProgress({ phase: "analyze", percent: 0, message: "Analyse wird gestartet …" });
    try {
      const started = await startTouristPreview(uploadId);
      setPreviewJobId(started.job_id);
      syncUrl(uploadId, started.job_id);
      const job = await pollTouristImportJob(started.job_id, {
        untilStatuses: ["preview", "failed"],
        ...pollOpts(800),
      });
      if (job.status === "failed") {
        setProgress(job.progress);
        setError(job.error_message ?? "Die Dateiprüfung ist fehlgeschlagen.");
        return;
      }
      if (job.preview_summary) applyPreview(job.preview_summary);
      setProgress(job.progress);
      refreshRecentUploads();
    } catch (e) {
      setError(formatApiError(e, "Analyse fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  async function onRefreshPreview() {
    if (!uploadId) {
      setError("Kein Upload vorhanden – bitte Datei erneut hochladen und prüfen.");
      return;
    }
    setBusy(true);
    setError(null);
    setProgress({ phase: "analyze", percent: 0, message: "Vorschau wird aktualisiert …" });
    try {
      const started = await startTouristPreview(uploadId, currentMappingsPayload());
      setPreviewJobId(started.job_id);
      syncUrl(uploadId, started.job_id);
      const job = await pollTouristImportJob(started.job_id, {
        untilStatuses: ["preview", "failed"],
        ...pollOpts(800),
      });
      if (job.status === "failed") {
        setProgress(job.progress);
        setError(job.error_message ?? "Vorschau fehlgeschlagen.");
        return;
      }
      if (job.preview_summary) {
        applyPreview(job.preview_summary, currentSavedMappings());
      }
      setProgress(job.progress);
      refreshRecentUploads();
    } catch (e) {
      setError(formatApiError(e, "Vorschau fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  const showProgress = Boolean(progress && (busy || resuming));

  async function onRunImport() {
    if (!uploadId || !preview) return;
    const incomplete = placeMappings.filter((m) => !m.target_place_id);
    if (incomplete.length > 0) {
      setError("Bitte allen Tourist-Plätzen einen Stellplatz zuordnen.");
      return;
    }
    setBusy(true);
    setError(null);
    setProgress({ phase: "import", percent: 0, message: "Import wird gestartet …" });
    try {
      const started = await startTouristImport(uploadId, currentMappingsPayload());
      syncUrl(uploadId, started.job_id);
      setProgress(started.progress);

      const job = await pollTouristImportJob(started.job_id, {
        untilStatuses: ["completed", "failed"],
        ...pollOpts(1000),
      });

      if (job.status === "failed") {
        setProgress(job.progress);
        setError(job.error_message ?? "Import fehlgeschlagen.");
        return;
      }

      await finishImportJob(job);
    } catch (e) {
      setError(formatApiError(e, "Import fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  async function onCancelImport() {
    const jobId = previewJobId;
    if (!jobId) return;
    setCancellingImport(true);
    setError(null);
    try {
      const job = await cancelTouristImportJob(jobId);
      setProgress(job.progress);
      setError(job.error_message ?? "Import abgebrochen.");
      setBusy(false);
      setResuming(false);
    } catch (e) {
      setError(formatApiError(e, "Import konnte nicht abgebrochen werden."));
    } finally {
      setCancellingImport(false);
    }
  }

  function updatePlaceMapping(plaid: number, targetPlaceId: string) {
    setSaveMessage(null);
    setPlaceMappings((rows) =>
      rows.map((row) =>
        row.tourist_plaid === plaid ? { ...row, target_place_id: targetPlaceId } : row,
      ),
    );
  }

  function updateArticleMapping(artid: number, targetArticleId: string) {
    setSaveMessage(null);
    setArticleMappings((rows) =>
      rows.map((row) =>
        row.tourist_artid === artid ? { ...row, target_article_id: targetArticleId } : row,
      ),
    );
  }

  function updateCategoryMapping(code: string, targetId: string) {
    setSaveMessage(null);
    setCategoryMappings((rows) =>
      rows.map((row) => (row.tourist_code === code ? { ...row, target_id: targetId } : row)),
    );
  }

  function updateArrivalTypeMapping(code: string, targetId: string) {
    setSaveMessage(null);
    setArrivalTypeMappings((rows) =>
      rows.map((row) => (row.tourist_code === code ? { ...row, target_id: targetId } : row)),
    );
  }

  async function onSavePlaceMappings() {
    if (!uploadId) {
      setError("Kein Upload vorhanden – bitte Datei hochladen oder unter „Aktuelle Uploads“ fortsetzen.");
      return;
    }
    if (busy || resuming) {
      setError("Bitte warten, bis Analyse oder Import abgeschlossen ist – danach können Sie die Zuordnung speichern.");
      return;
    }
    setSavingMappings(true);
    setError(null);
    setSaveMessage(null);
    try {
      const saved = await saveTouristUploadMappings(uploadId, currentMappingsPayload());
      if (preview) {
        setPlaceMappings(buildPlaceMappings(preview, saved.place_mappings));
        setArticleMappings(buildArticleMappings(preview, saved.article_mappings));
        setPreview((prev) =>
          prev
            ? {
                ...prev,
                place_mapping_issues: saved.place_mapping_issues ?? [],
                missing_place_plaids: [],
              }
            : prev,
        );
      }
      const session = await getTouristUploadSession(uploadId);
      if (session.preview_job?.id) {
        setPreviewJobId(session.preview_job.id);
        syncUrl(uploadId, session.preview_job.id);
      }
      setSaveMessage("Zuordnungen wurden gespeichert.");
    } catch (e) {
      setError(formatApiError(e, "Zuordnung konnte nicht gespeichert werden."));
    } finally {
      setSavingMappings(false);
    }
  }

  async function onReconcileArticleMappings() {
    setReconcilingArticles(true);
    setError(null);
    setReconcileMessage(null);
    try {
      const result: TouristImportReconcileArticlesResult = await reconcileTouristArticleMappings();
      if (result.lines_updated > 0) {
        setReconcileMessage(
          `${result.lines_updated.toLocaleString("de-DE")} Rechnungspositionen wurden mit CampDesk-Artikeln verknüpft.`,
        );
      } else if (result.lines_scanned === 0) {
        setReconcileMessage("Keine offenen Legacy-Positionen gefunden.");
      } else if (result.mappings_available === 0) {
        setReconcileMessage(
          "Keine gespeicherten Artikel-Zuordnungen vorhanden – bitte zuerst zuordnen und speichern.",
        );
      } else {
        setReconcileMessage(
          `${result.lines_scanned.toLocaleString("de-DE")} Legacy-Positionen ohne passende Zuordnung.`,
        );
      }
    } catch (e) {
      setError(formatApiError(e, "Artikel-Verknüpfung konnte nicht durchgeführt werden."));
    } finally {
      setReconcilingArticles(false);
    }
  }

  const rowCountEntries = useMemo(
    () => Object.entries(preview?.row_counts ?? {}).filter(([, n]) => n > 0),
    [preview],
  );

  const mappedCount = placeMappings.filter((m) => m.target_place_id).length;
  const mappedArticleCount = articleMappings.filter((m) => m.target_article_id).length;

  const sortedArticles = useMemo(
    () =>
      [...articles].sort((a, b) =>
        `${a.article_number} ${a.name}`.localeCompare(`${b.article_number} ${b.name}`, "de"),
      ),
    [articles],
  );

  if (!canEdit && tab === "import") {
    return <StammdatenWriteHint />;
  }

  if (tab !== "import") {
    return (
      <div className="space-y-6">
        <nav className="flex flex-wrap gap-2">
          {TOURIST_IMPORT_TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={classNames(
                "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                tab === item.id
                  ? "bg-accent text-white"
                  : "bg-fill text-label-secondary hover:bg-fill-secondary",
              )}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {tab === "sold-out" ? <TouristImportSoldOutPanel canWrite={canEdit} /> : null}
        {tab === "pages" ? <TouristPanelPagesPanel /> : null}
        {tab === "devices" ? <TouristPanelWorkstationsPanel /> : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap gap-2">
        {TOURIST_IMPORT_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={classNames(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
              tab === item.id
                ? "bg-accent text-white"
                : "bg-fill text-label-secondary hover:bg-fill-secondary",
            )}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>
      {recentUploads.length > 0 ? (
        <Card title="Aktuelle Uploads" bodyClassName="space-y-3">
          <p className="text-sm text-label-secondary">
            Hochgeladene SQL-Dumps und Vorschauen sind auf allen Geräten sichtbar (24 Stunden
            gültig). Wählen Sie einen Eintrag, um die Zuordnung fortzusetzen.
          </p>
          <ul className="divide-y divide-separator rounded-lg border border-separator">
            {recentUploads.map((item) => {
              const isActive = item.id === uploadId;
              const hasPreview = item.latest_preview_job_status === "preview";
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`flex w-full flex-col gap-1 px-3 py-3 text-left text-sm transition-colors hover:bg-fill sm:flex-row sm:items-center sm:justify-between ${
                      isActive ? "bg-fill" : ""
                    }`}
                    disabled={busy || resuming}
                    onClick={() => void resumeUploadSession(item.id)}
                  >
                    <span>
                      <span className="font-medium text-label">{item.filename}</span>
                      <span className="ml-2 text-label-tertiary">
                        {formatDateTimeShort(item.created_at)}
                      </span>
                    </span>
                    <span className="text-label-secondary">
                      {item.status === "ready"
                        ? hasPreview
                          ? "Vorschau bereit"
                          : "Bereit zur Prüfung"
                        : item.status === "uploading"
                          ? `Upload ${item.progress.percent} %`
                          : item.status}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      {showProgress ? (
        <Card title="Fortschritt" bodyClassName="space-y-2">
          <ProgressBar percent={progress!.percent} label={progressLabel(progress)} />
          {connectionRetryCount > 0 ? (
            <p className="text-xs text-status-amber">
              Kurzzeitige Verbindungsprobleme – der Import läuft auf dem Server weiter. Abfrage
              wird automatisch wiederholt ({connectionRetryCount}).
            </p>
          ) : progress?.message?.includes(" / ") ? (
            <p className="text-xs text-label-tertiary">
              Der Zähler in Klammern zeigt verarbeitete Zeilen. Bleibt er länger unverändert,
              hängt der Job vermutlich – nicht nur weil viele Datensätze anstehen.
            </p>
          ) : null}
          {busy &&
          previewJobId &&
          (progress?.phase === "import" || progress?.phase === "analyze") ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                type="button"
                variant="secondary"
                loading={cancellingImport}
                disabled={cancellingImport}
                onClick={() => void onCancelImport()}
              >
                Import abbrechen
              </Button>
              <p className="text-xs text-label-tertiary">
                Bleibt der Fortschritt länger unverändert (z. B. nach Server-Neustart), Seite neu
                laden oder abbrechen und Import erneut starten.
              </p>
            </div>
          ) : null}
        </Card>
      ) : null}

      <Card title="SQL-Dump hochladen" bodyClassName="space-y-4">
        <p className="text-sm text-label-secondary">
          Große SQL-Dumps (z. B. über 200 MB) werden zuerst auf den Server hochgeladen und dort
          analysiert. Der Fortschritt wird serverseitig gespeichert – auf jedem Gerät sichtbar,
          solange Sie eingeloggt sind.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
            <span className="font-medium text-label">Datei</span>
            <input
              type="file"
              accept=".sql,application/sql,text/plain"
              onChange={onFileChange}
              disabled={busy || resuming}
              className="block w-full text-sm text-label file:mr-3 file:rounded-lg file:border-0 file:bg-fill file:px-3 file:py-2 file:text-sm file:font-medium file:text-label"
            />
          </label>
          <Button
            type="button"
            onClick={() => void onUploadAndPreview()}
            loading={busy}
            disabled={!file || resuming}
          >
            Hochladen &amp; prüfen
          </Button>
        </div>
        {file ? (
          <p className="text-xs text-label-tertiary">
            Ausgewählt: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            {uploadId ? ` · Upload-ID ${uploadId.slice(0, 8)}…` : null}
            {previewJobId ? ` · Prüf-Job ${previewJobId.slice(0, 8)}…` : null}
          </p>
        ) : uploadId ? (
          <p className="text-xs text-label-tertiary">
            Server-Upload: {uploadId.slice(0, 8)}…
            {previewJobId ? ` · Prüf-Job ${previewJobId.slice(0, 8)}…` : null}
          </p>
        ) : null}
        {resuming ? (
          <p className="text-sm text-label-secondary">Import-Sitzung wird geladen …</p>
        ) : null}
        {uploadId && !preview && !busy && !resuming ? (
          <Button type="button" variant="secondary" onClick={() => void onStartPreviewOnly()}>
            Analyse starten
          </Button>
        ) : null}
      </Card>

      {error ? (
        <Card bodyClassName="text-sm text-status-red">{error}</Card>
      ) : null}

      {preview ? (
        <>
          <Card title="Vorschau" bodyClassName="space-y-4">
            {preview.warnings.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-sm text-status-amber">
                {preview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}

            {rowCountEntries.length > 0 ? (
              <div>
                <h3 className="mb-2 text-sm font-medium text-label">Datensätze im Dump</h3>
                <ul className="grid gap-1 text-sm sm:grid-cols-2">
                  {rowCountEntries.map(([table, count]) => (
                    <li key={table} className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                      <span>{TOURIST_TABLE_LABELS[table] ?? table}</span>
                      <span className="font-medium tabular-nums">{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {preview.place_mapping_issues.length > 0 ? (
              <div>
                <h3 className="mb-2 text-sm font-medium text-status-amber">Hinweise zur Platz-Zuordnung</h3>
                <ul className="list-disc space-y-1 pl-5 text-sm text-status-amber">
                  {preview.place_mapping_issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {preview.article_mapping_issues.length > 0 ? (
              <div>
                <h3 className="mb-2 text-sm font-medium text-status-amber">Hinweise zur Artikel-Zuordnung</h3>
                <ul className="list-disc space-y-1 pl-5 text-sm text-status-amber">
                  {preview.article_mapping_issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {preview.files.some((f) => f.present && f.columns_ok === false) ? (
              <div>
                <h3 className="mb-2 text-sm font-medium text-status-red">Tabellenprüfung</h3>
                <ul className="space-y-1 text-sm text-status-red">
                  {preview.files
                    .filter((f) => f.present && f.columns_ok === false)
                    .map((f) => (
                      <li key={f.name}>
                        {f.name}: fehlende Spalten{" "}
                        {(f.missing_columns ?? []).join(", ") || "—"}
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </Card>

          {placeMappings.length > 0 ? (
            <Card title="Plätze zuordnen" bodyClassName="space-y-4">
              <p className="text-sm text-label-secondary">
                Ordnen Sie jeden Tourist-Platz aus dem Dump einem Stellplatz in CampDesk zu.
                Automatische Vorschläge werden vorausgefüllt; fehlende Zuordnungen müssen Sie
                manuell wählen ({mappedCount} von {placeMappings.length} zugeordnet).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[28rem] text-left text-sm">
                  <thead>
                    <tr className="border-b border-separator text-label-secondary">
                      <th className="py-2 pr-3 font-medium">Tourist</th>
                      <th className="py-2 font-medium">CampDesk-Platz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placeMappings.map((row) => (
                      <tr
                        key={row.tourist_plaid}
                        className={`border-b border-separator/60 ${
                          !row.target_place_id ? "bg-status-amber/5" : ""
                        }`}
                      >
                        <td className="py-2 pr-3">
                          <span className="font-medium">{row.tourist_planr}</span>
                          <span className="ml-1 text-label-tertiary">(ID {row.tourist_plaid})</span>
                        </td>
                        <td className="py-2">
                          <select
                            className="eh-input w-full max-w-xs"
                            value={row.target_place_id}
                            onChange={(e) => updatePlaceMapping(row.tourist_plaid, e.target.value)}
                          >
                            <option value="">— wählen —</option>
                            {sortPitchesByTouristPlanr(pitches, row.tourist_planr).map((p) => (
                              <option key={p.id} value={normalizePlaceId(p.id)}>
                                {p.code}
                                {p.label ? ` – ${p.label}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {busy && progress?.phase === "import" ? (
                <ProgressBar percent={progress.percent} label={progressLabel(progress)} />
              ) : null}
              {saveMessage ? (
                <p className="text-sm text-status-green">{saveMessage}</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void onSavePlaceMappings()}
                  loading={savingMappings}
                  disabled={!uploadId || busy || resuming}
                >
                  Zuordnung speichern
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void onRefreshPreview()}
                  loading={busy}
                  disabled={!uploadId}
                >
                  Vorschau aktualisieren
                </Button>
                <Button
                  type="button"
                  onClick={() => void onRunImport()}
                  loading={busy}
                  disabled={placeMappings.some((m) => !m.target_place_id)}
                >
                  Import starten
                </Button>
              </div>
            </Card>
          ) : (
            <Card bodyClassName="space-y-3">
              <p className="text-sm text-label-secondary">
                Keine Tourist-Plätze im Dump gefunden – Sie können den Import direkt starten.
              </p>
              {busy && progress?.phase === "import" ? (
                <ProgressBar percent={progress.percent} label={progressLabel(progress)} />
              ) : null}
              <Button type="button" onClick={() => void onRunImport()} loading={busy}>
                Import starten
              </Button>
            </Card>
          )}

          {articleMappings.length > 0 ? (
            <Card title="Artikel zuordnen" bodyClassName="space-y-4">
              <p className="text-sm text-label-secondary">
                Ordnen Sie Tourist-Artikel aus dem Dump den CampDesk-Artikeln zu. Die Zuordnung
                ist optional, aber für die Preissimulation mit historischen Umsätzen
                erforderlich – ohne Mapping werden Positionen als Legacy importiert (
                {mappedArticleCount} von {articleMappings.length} zugeordnet).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[32rem] text-left text-sm">
                  <thead>
                    <tr className="border-b border-separator text-label-secondary">
                      <th className="py-2 pr-3 font-medium">Tourist-Artikel</th>
                      <th className="py-2 font-medium">CampDesk-Artikel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articleMappings.map((row) => (
                      <tr
                        key={row.tourist_artid}
                        className={`border-b border-separator/60 ${
                          !row.target_article_id ? "bg-status-amber/5" : ""
                        }`}
                      >
                        <td className="py-2 pr-3">
                          <span className="font-medium">{row.label}</span>
                          {row.tourist_artnr ? (
                            <span className="ml-1 text-label-tertiary">({row.tourist_artnr})</span>
                          ) : null}
                          <span className="ml-1 text-label-tertiary">(ID {row.tourist_artid})</span>
                        </td>
                        <td className="py-2">
                          <select
                            className="eh-input w-full max-w-md"
                            value={row.target_article_id}
                            onChange={(e) =>
                              updateArticleMapping(row.tourist_artid, e.target.value)
                            }
                          >
                            <option value="">— keine Zuordnung —</option>
                            {sortedArticles.map((a) => (
                              <option key={a.id} value={normalizePlaceId(a.id)}>
                                {a.article_number} – {a.name}
                                {!a.is_active ? " (inaktiv)" : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reconcileMessage ? (
                <p className="text-sm text-status-green">{reconcileMessage}</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void onSavePlaceMappings()}
                  loading={savingMappings}
                  disabled={!uploadId || busy || resuming}
                >
                  Zuordnungen speichern
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void onReconcileArticleMappings()}
                  loading={reconcilingArticles}
                  disabled={busy || resuming || mappedArticleCount === 0}
                >
                  Bereits importierte Positionen verknüpfen
                </Button>
              </div>
              <p className="text-xs text-label-tertiary">
                Nach einem früheren Import ohne Artikel-Zuordnung können Sie gespeicherte Mappings
                nachträglich auf Legacy-Rechnungspositionen anwenden (z. B. für die Preissimulation).
              </p>
            </Card>
          ) : null}

          {categoryMappings.length > 0 ? (
            <Card title="Platzkategorien zuordnen" bodyClassName="space-y-4">
              <p className="text-sm text-label-secondary">
                In den Gastdaten (adr14) stehen Kürzel für die Platzkategorie der nächsten
                Buchung. Ordnen Sie die Kürzel den CampDesk-Platzkategorien zu – Reservierungen
                erhalten dann die Kategorie. Gäste mit * am Namen behalten ihren festen Platz,
                alle anderen sind innerhalb der Kategorie frei platzierbar. Die Zuordnung ist
                optional ({categoryMappings.filter((m) => m.target_id).length} von{" "}
                {categoryMappings.length} zugeordnet).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[28rem] text-left text-sm">
                  <thead>
                    <tr className="border-b border-separator text-label-secondary">
                      <th className="py-2 pr-3 font-medium">Tourist-Kürzel</th>
                      <th className="py-2 pr-3 font-medium">Gäste</th>
                      <th className="py-2 font-medium">CampDesk-Platzkategorie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryMappings.map((row) => (
                      <tr key={row.tourist_code} className="border-b border-separator/60">
                        <td className="py-2 pr-3 font-medium">{row.tourist_code}</td>
                        <td className="py-2 pr-3 tabular-nums text-label-secondary">
                          {row.guest_count > 0 ? row.guest_count.toLocaleString("de-DE") : "—"}
                        </td>
                        <td className="py-2">
                          <select
                            className="eh-input w-full max-w-xs"
                            value={row.target_id}
                            onChange={(e) =>
                              updateCategoryMapping(row.tourist_code, e.target.value)
                            }
                          >
                            <option value="">— keine Zuordnung —</option>
                            {pitchCategories.map((c) => (
                              <option key={c.id} value={normalizePlaceId(c.id)}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pitchCategories.length === 0 ? (
                <p className="text-xs text-status-amber">
                  Keine Platzkategorien vorhanden – legen Sie diese zuerst an.
                </p>
              ) : null}
            </Card>
          ) : null}

          {arrivalTypeMappings.length > 0 ? (
            <Card title="Anreisearten zuordnen" bodyClassName="space-y-4">
              <p className="text-sm text-label-secondary">
                In den Gastdaten (adr22) stehen Kürzel für die Anreiseart (z. B. MC =
                Wohnmobil, WW = Wohnwagen, ZELT). Ordnen Sie die Kürzel den
                CampDesk-Anreisearten zu – importierte Buchungen erhalten dann die Anreiseart;
                die Fahrzeuglänge (adr15) wird automatisch übernommen. Die Zuordnung ist
                optional ({arrivalTypeMappings.filter((m) => m.target_id).length} von{" "}
                {arrivalTypeMappings.length} zugeordnet).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[28rem] text-left text-sm">
                  <thead>
                    <tr className="border-b border-separator text-label-secondary">
                      <th className="py-2 pr-3 font-medium">Tourist-Kürzel</th>
                      <th className="py-2 pr-3 font-medium">Gäste</th>
                      <th className="py-2 font-medium">CampDesk-Anreiseart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {arrivalTypeMappings.map((row) => (
                      <tr key={row.tourist_code} className="border-b border-separator/60">
                        <td className="py-2 pr-3 font-medium">{row.tourist_code}</td>
                        <td className="py-2 pr-3 tabular-nums text-label-secondary">
                          {row.guest_count > 0 ? row.guest_count.toLocaleString("de-DE") : "—"}
                        </td>
                        <td className="py-2">
                          <select
                            className="eh-input w-full max-w-xs"
                            value={row.target_id}
                            onChange={(e) =>
                              updateArrivalTypeMapping(row.tourist_code, e.target.value)
                            }
                          >
                            <option value="">— keine Zuordnung —</option>
                            {arrivalTypes.map((t) => (
                              <option key={t.id} value={normalizePlaceId(t.id)}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {saveMessage ? (
                <p className="text-sm text-status-green">{saveMessage}</p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={() => void onSavePlaceMappings()}
                loading={savingMappings}
                disabled={!uploadId || busy || resuming}
              >
                Zuordnungen speichern
              </Button>
            </Card>
          ) : null}
        </>
      ) : null}

      {runResult ? (
        <Card title="Import abgeschlossen" bodyClassName="space-y-4">
          <div
            className={`rounded-lg px-4 py-3 ${
              runResult.error_count > 0
                ? "border border-status-amber/40 bg-status-amber/10"
                : "border border-status-green/40 bg-status-green/10"
            }`}
          >
            <p
              className={`text-base font-medium ${
                runResult.error_count > 0 ? "text-status-amber" : "text-status-green"
              }`}
            >
              {runResult.error_count > 0
                ? "Import abgeschlossen mit Fehlern"
                : "Import erfolgreich abgeschlossen"}
            </p>
            <p className="mt-1 text-sm text-label-secondary">
              Status: {runResult.status}
              {runResult.error_count > 0
                ? ` · ${runResult.error_count.toLocaleString("de-DE")} Fehler`
                : " · keine Fehler"}
              {importErrorReport && importErrorReport.suppressed > 0
                ? ` (${importErrorReport.suppressed.toLocaleString("de-DE")} nur aggregiert protokolliert)`
                : null}
            </p>
          </div>

          {importStats ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-label">Daten in der Datenbank</p>
              <ul className="grid gap-1 text-sm sm:grid-cols-2">
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Gäste</span>
                  <span className="font-medium tabular-nums">
                    {importStats.in_database.guests.toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Buchungen gesamt</span>
                  <span className="font-medium tabular-nums">
                    {importStats.in_database.bookings_total.toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Rechnungen</span>
                  <span className="font-medium tabular-nums">
                    {importStats.in_database.invoices.toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Rechnungspositionen</span>
                  <span className="font-medium tabular-nums">
                    {importStats.in_database.invoice_lines.toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Zahlungen</span>
                  <span className="font-medium tabular-nums">
                    {(importStats.in_database.payments ?? 0).toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Platzbelegungen</span>
                  <span className="font-medium tabular-nums">
                    {importStats.in_database.pitch_occupancies.toLocaleString("de-DE")}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                  <span>Import-Läufe abgeschlossen</span>
                  <span className="font-medium tabular-nums">
                    {importStats.completed_import_jobs.toLocaleString("de-DE")}
                  </span>
                </li>
              </ul>
              {importStats.by_source.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-separator">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-grouped">
                      <tr>
                        <th className="px-2 py-1 font-medium">Quelle</th>
                        <th className="px-2 py-1 font-medium text-right">In DB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importStats.by_source.map((row) => (
                        <tr key={row.source_table} className="border-t border-separator/60">
                          <td className="px-2 py-1">{row.label}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {row.count_in_database.toLocaleString("de-DE")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

          {importStats?.diagnostics?.hints && importStats.diagnostics.hints.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-label">Hinweise</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-label-secondary">
                {importStats.diagnostics.hints.map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {importErrorReport && importErrorReport.place_mappings_used > 0 ? (
            <p className="text-xs text-label-secondary">
              Beim Import waren {importErrorReport.place_mappings_used} Platz-Zuordnungen aktiv.
            </p>
          ) : null}
          {errorHint(importErrorReport) ? (
            <p className="rounded-lg border border-status-amber/30 bg-status-amber/5 px-3 py-2 text-sm text-label">
              {errorHint(importErrorReport)}
            </p>
          ) : null}
          <p className="text-sm font-medium text-label">Dieser Lauf</p>
          <ul className="grid gap-1 text-sm sm:grid-cols-2">
            {Object.entries(runResult.stats)
              .filter(([key]) => !key.startsWith("error_") || key === "errors" || key === "errors_suppressed")
              .filter(([key]) => key !== "error_summary")
              .map(([key, value]) => (
              <li key={key} className="flex justify-between gap-2 rounded-lg bg-fill px-3 py-2">
                <span>{formatImportStatKey(key)}</span>
                <span className="font-medium tabular-nums">
                  {typeof value === "number" ? value.toLocaleString("de-DE") : String(value)}
                </span>
              </li>
            ))}
          </ul>
          {importErrorSummary.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-label-secondary">
                Fehler nach Meldung gruppiert (Top {importErrorSummary.length})
                {importErrorReport
                  ? ` · ${importErrorReport.logged_samples.toLocaleString("de-DE")} Beispielzeilen in der DB`
                  : null}
              </p>
              <div className="max-h-80 overflow-y-auto rounded-lg border border-separator">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-grouped">
                    <tr>
                      <th className="px-2 py-1">Tabelle</th>
                      <th className="px-2 py-1">Feld</th>
                      <th className="px-2 py-1">Anzahl</th>
                      <th className="px-2 py-1">Beispiel</th>
                      <th className="px-2 py-1">Meldung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importErrorSummary.map((row) => (
                      <tr
                        key={`${row.source_table}:${row.field_name ?? ""}:${row.message}`}
                        className="border-t border-separator/60"
                      >
                        <td className="px-2 py-1 whitespace-nowrap">{formatSourceTable(row.source_table)}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{row.field_name ?? "—"}</td>
                        <td className="px-2 py-1 tabular-nums">{row.count.toLocaleString("de-DE")}</td>
                        <td className="px-2 py-1 whitespace-nowrap text-label-secondary">
                          {row.example_source_id
                            ? `ID ${row.example_source_id}`
                            : row.example_source_row
                              ? `Zeile ${row.example_source_row}`
                              : "—"}
                        </td>
                        <td className="px-2 py-1">{row.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {importErrors.length > 0 ? (
                <details className="text-xs text-label-secondary">
                  <summary className="cursor-pointer py-1">
                    Einzelbeispiele ({importErrors.length} von{" "}
                    {importErrorReport?.logged_samples.toLocaleString("de-DE") ?? "?"})
                  </summary>
                  <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-separator p-2">
                    {importErrors.map((row) => (
                      <li key={row.id}>
                        [{formatSourceTable(row.source_table)}] {row.message}
                        {row.source_id ? ` (ID ${row.source_id})` : null}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
